/**
 * 狛江市医療・介護・地域資源マップ検索の検索結果画面
 *
 * * App
 *     * [KomaeResult]{@link module:KomaeResult~KomaeResult}
 *     * [KomaeMap]{@link module:KomaeMap~KomaeMap}
 *         * [SearchNextButton]{@link module:KomaeResult~SearchNextButton}
 *
 * @module KomaeResult
 */
import React from 'react';
import { Row, Col, Alert, Button, Card, Spinner } from 'react-bootstrap';
import { BsArrowClockwise } from 'react-icons/bs';

import * as KomaeConfig from './KomaeConfig';
import KomaeMap from './KomaeMap';

/**
 * 「検索中...」表示の React Component
 *
 * ```js
 * return (
 *   <SearchNextButton nextSearch={nextSearchCallback} />
 * );
 * ```
 * nextSearch は [App#nextSearch]{@link module:App~App#nextSearch} への callback。
 */
class SearchNextButton extends React.Component {
  constructor(props) {
    super(props);
    this.myRef = React.createRef();
  }

  componentDidMount() {
    // IntersectionOverver を使って画面に現れたことを検出したら検索処理
    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // サーバに負荷をかけないように検索開始まで 250ms 待つ
          setTimeout( () => this.props.nextSearch(), 250);
        }
      },
      { threshold: 0.5 }
    );
    this.observer.observe(this.myRef.current);
  }

  componentWillUnmount() {
    this.observer.unobserve(this.myRef.current);
  }

  render() {
    return <Button ref={this.myRef} variant="light" className="mb-2"><Spinner animation="border" size="sm" role="status" /> 検索中...</Button>;
  }
}

/**
 * 検索結果画面の Resct Component
 *
 * ```js
 * return (
 *   <KomaeResult
 *     nextSearch={nextSearchCallback}
 *     searchState={state}
 *   />
 * );
 * ```
 *
 * nextSearch は [App#nextSearch]{@link module:App~App#nextSearch} への
 * callback。 [SearchNextButton]{@link module:KomaeResult~SearchNextButton}に渡
 * される。
 *
 * searchState には [App#state]{@link module:App~App#state} を渡す。
 */
class KomaeResult extends React.Component {
  componentDidUpdate() {
    const state = this.props.searchState;
    window.sessionStorage.setItem("state", JSON.stringify(state));
  }

  // 地図ボタンクリック。地図に切り替え、残りの全部の検索を開始。
  onClickMapButton = e => {
    window.history.pushState({ mode:"map", keyword: this.props.searchState.keyword, categories: this.props.searchState.categories }, '', '');
    this.props.setMode("map");

    setTimeout( () => {
      const vh = window.innerHeight;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    }, 1000)

    const searchNext = () => {
      setTimeout( () => {
        if (this.props.searchState.hasNext && this.props.searchState.mode === "map") {
          if (!this.props.searchState.searching) {
            this.props.nextSearch();
          }
          searchNext();
        }
      }, 250 );
    }
    searchNext();
  }

  // 「戻る」Button
  onClickBackButton = () => window.history.back();

  // render
  render() {
    let result;
    result = this.props.searchState.spots.map((spot, idx) => {
      return (
        <Col key={idx} xs={6} sm={4} md={3} lg={2} className="resultCol">
        <a href={KomaeConfig.KOKOSIL_TOP + "/ja/place/" + spot.ucode }>
        <Card>
        <Card.Img variant="top" src={spot.thumbnail || "./images/no-image.jpg"} />
        <Card.Body>
        <Card.Title>{spot.name}</Card.Title>
        <Card.Text>{spot.description}</Card.Text>
        </Card.Body>
        </Card>
        </a>
        </Col>
      );
    });
    if (result.length === 0 && !this.props.searchState.searching && !this.props.searchState.searchError) {
      result = <Col>検索結果は 0 件です。</Col>
    }

    let message;
    if (this.props.searchState.searchError) {
      message = (
        <Col>
        <Alert variant="danger">通信エラーが発生しました。{" "}
        <Button variant="danger" onClick={this.props.nextSearch} ><BsArrowClockwise /></Button>
        </Alert>
        </Col>
      );
    }
    else if (this.props.searchState.searching) {
      message = (
        <Col><Button variant="light" className="mb-2"><Spinner animation="border" size="sm" role="status" /> 検索中...</Button></Col>
      );
    }
    else if (this.props.searchState.hasNext) {
      message = (
        <Col><SearchNextButton nextSearch={this.props.nextSearch} /></Col>
      );
    }
    else {
      message = null;
    }

    let mapButtons;
    if (window.isIE) { // IE11 は react-google-maps/api が動作しないのでココシルに飛ばす
      const kwd = this.props.searchState.keyword || "";
      const categories = this.props.searchState.categories || "category2=0";
      const url = KomaeConfig.KOKOSIL_TOP + "/ja/map?" + categories.replace(/category2/g, "c2") + "&q=" + kwd; //encodeURIComponent(kwd);
      mapButtons = (
        <>
        <Button id="result-back-btn" variant="primary" onClick={this.onClickBackButton}>戻る</Button>
        <a className="btn btn-success" id="map-btn" href={url}>地図で表示する</a>
        </>
      );
    }
    else {
      if (this.props.searchState.mode === "result") {
        mapButtons = (
          <>
          <Button id="result-back-btn" variant="primary" onClick={this.onClickBackButton}>戻る</Button>
          <Button variant="success" id="map-btn" onClick={this.onClickMapButton}>地図で表示する</Button>
          </>
        );
      }
      else { // 地図画面
        if (this.props.searchState.hasNext) {
          mapButtons = <Button id="map-loading-btn" variant="secondary"><Spinner animation="border" size="sm" role="status" /></Button>;
        }
        else {
          mapButtons = <Button id="map-back-btn" variant="primary" onClick={this.onClickBackButton}>戻る</Button>;
        }
      }
    }

    const resultListHidden = this.props.searchState.mode !== "result" ? " hidden" : "";
    const resultMapHidden  = this.props.searchState.mode !== "map"    ? " hidden" : "";
    return (
      <>
      <div className="map-btn-div">
        <Col id="map-btn-col">{mapButtons}</Col>
      </div>
      <div className={"resultList" + resultListHidden}>
        <Row className="result-div">{result}</Row>
        <Row className="mt-2 mb-2">{message}</Row>
      </div>
      {!resultMapHidden && (
      <div className={"resultMap" + resultMapHidden}>
        <KomaeMap searchState={this.props.searchState} />
      </div>
      )}
      </>
    );
  }
}

export default KomaeResult;
